import React from 'react';
import {
  Rocket,
  Package,
  BookOpen,
  Map,
  Image,
  Server,
  Mail,
  User,
  Zap,
  Gamepad2,
  BarChart3,
  Settings,
  Crown,
  Shield,
  Construction,
  Home,
  Users,
  PartyPopper,
  Globe,
  Sparkles,
  Sword,
  Eye,
  Telescope,
  Castle,
  Palmtree,
  Mountain,
  Trees,
  Building,
  Building2,
  Waves,
  MessageCircle,
  Github,
  Twitter,
  Youtube,
  Send,
  Flame,
  Target,
  Award,
  Heart,
  Code,
  Blocks,
  FileCode,
  Database,
  Cloud,
  Leaf,
  EyeOff,
  Activity,
  PawPrint,
  SquircleDashed,
  // Auth icons
  LogIn,
  LogOut,
  UserPlus,
  AlertCircle,
  X,
  Check,
  Info,
  Key,
  ChevronDown,
  Menu,
  // Dashboard icons
  Upload,
  Download,
  Trash2,
  Copy,
  Calendar,
  Edit,
  Lock,
  Monitor,
  Clock,
  RefreshCw,
  // News & Editor icons
  Newspaper,
  Plus,
  Search,
  Star,
  ChevronLeft,
  ChevronRight,
  Save,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List,
  ListOrdered,
  Link,
  Unlink,
  Minus,
  Eraser,
  Undo2,
  Redo2,
  Share2,
  Tag,
} from 'lucide-react';
import styles from './Icon.module.css';

/**
 * Icon Component - Sistema centralizado de iconos SVG
 * Usa lucide-react para iconos profesionales y consistentes
 * Soporta animaciones optimizadas para desktop y mobile
 */

// Mapping semántico: nombre => componente Lucide
const iconMap = {
  // Navegación principal
  'home': Home,
  'features': Rocket,
  'modpacks': Package,
  'lore': BookOpen,
  'roadmap': Map,
  'gallery': Image,
  'server': Server,
  'contact': Mail,
  
  // Launcher features
  'launcher': Rocket,
  'profile': User,
  'auto-update': Zap,
  'gamepad': Gamepad2,
  'stats': BarChart3,
  'settings': Settings,
  
  // Roles del servidor
  'role-admin': Crown,
  'role-moderator': Shield,
  'role-builder': Construction,
  'role-explorer': Telescope,
  'role-member': User,
  
  // Lore & Characters
  'ppkeash': Crown,           // 👑 Estratega del Reino
  'microjd': Settings,        // ⚙️ Arquitecto Tecnológico  
  'alainorlu': Telescope,     // 🔭 Mago Errante
  'foxtans': Package,         // 📦 Comerciante Interdimensional
  'bruno': Zap,               // ⚡ Portador de la Falla
  'sebas': Target,            // 🎯 Cazador de Almas
  'kiara': Sparkles,          // ✨ Hechicera del Eclipse
  'akimitzu': Leaf,           // 🍃 Protectora de Gaia
  
  // NUEVOS PERSONAJES
  'matt': EyeOff,             // 👁️ Exiliado de Nyctocosm
  'jagger': BookOpen,         // 📖 Archivista del Vacío
  'arkniz': Activity,         // ⚡ Marionetista de Sombras
  'sushiowo': Waves,          // 🌊 Navegante Abisal
  'figaro': PawPrint,         // 🐾 Domador de Criaturas
  'kipper': Sword,            // ⚔️ Guerrero del Tormento

  'dungeon-castle': Castle, // 🏰 Mazmorras antiguas
  'sword': Sword,           // ⚔️ The Last Sword
  'portal': Map,            // 🗺️ Portal Olvidado
  'survival': Shield,       // 🛡️ Supervivencia
  
  // Eventos
  'event-festival': PartyPopper,
  'event-raid': Sword,
  'event-build': Construction,
  'event-lore': BookOpen,
  
  // Modpacks temáticos
  'cosmic': Sparkles,
  'medieval': Castle,
  'dungeon': Sword,
  'nature': Trees,
  'tech-advanced': Settings,
  
  // Gallery / Screenshots
  'celestial': Eye,
  'ruins': Building2,
  'city': Building,
  'forest': Trees,
  'beach': Palmtree,
  'castle-floating': Castle,
  'ocean': Waves,
  'mountain': Mountain,
  'void': SquircleDashed,
  
  // Roadmap phases
  'phase-alpha': Rocket,
  'phase-beta': Users,
  'phase-release': PartyPopper,
  'phase-global': Globe,
  
  // Social links
  'discord': MessageCircle,
  'github': Github,
  'twitter': Twitter,
  'youtube': Youtube,
  'email': Send,
  
  // Tech stack / About
  'react': Code,
  'nodejs': Blocks,
  'vscode': FileCode,
  'database': Database,
  'cloud': Cloud,
  'fire': Flame,
  'heart': Heart,
  'award': Award,
  
  // Auth / UI icons
  'login': LogIn,
  'logout': LogOut,
  'register': UserPlus,
  'alert': AlertCircle,
  'close': X,
  'check': Check,
  'info': Info,
  'key': Key,
  'chevron-down': ChevronDown,
  'menu': Menu,
  
  // Dashboard icons
  'upload': Upload,
  'download': Download,
  'trash': Trash2,
  'copy': Copy,
  'calendar': Calendar,
  'edit': Edit,
  'lock': Lock,
  'monitor': Monitor,
  'clock': Clock,
  'refresh': RefreshCw,
  
  // News & Editor icons
  'news': Newspaper,
  'plus': Plus,
  'search': Search,
  'star': Star,
  'star-empty': Star,
  'chevron-left': ChevronLeft,
  'chevron-right': ChevronRight,
  'save': Save,
  'bold': Bold,
  'italic': Italic,
  'underline': Underline,
  'strikethrough': Strikethrough,
  'align-left': AlignLeft,
  'align-center': AlignCenter,
  'align-right': AlignRight,
  'list': List,
  'list-ordered': ListOrdered,
  'link': Link,
  'unlink': Unlink,
  'minus': Minus,
  'eraser': Eraser,
  'undo': Undo2,
  'redo': Redo2,
  'share': Share2,
  'tag': Tag,
  'eye': Eye,
  
  // Genéricos
  'default': Sparkles,
};

/**
 * Icon Component Props:
 * @param {string} name - Nombre semántico del icono (ej: 'launcher', 'role-admin')
 * @param {string} size - Tamaño: 'xs', 'sm', 'md', 'lg', 'xl' (default: 'md')
 * @param {string} animation - Tipo de animación: 'float', 'pulse', 'spin', 'bounce', 'none' (default: 'none')
 * @param {string} color - Color personalizado (default: hereda del padre)
 * @param {string} className - Clases CSS adicionales
 * @param {object} style - Estilos inline adicionales
 */
function Icon({ 
  name = 'default', 
  size = 'md', 
  animation = 'none', 
  color,
  className = '',
  style = {},
  ...props 
}) {
  const IconComponent = iconMap[name] || iconMap['default'];
  
  // Clases dinámicas
  const iconClasses = [
    styles.icon,
    styles[`size-${size}`],
    animation !== 'none' ? styles[`anim-${animation}`] : '',
    className
  ].filter(Boolean).join(' ');
  
  // Estilos inline dinámicos
  const iconStyle = {
    ...style,
    ...(color && { color })
  };
  
  return (
    <IconComponent 
      className={iconClasses}
      style={iconStyle}
      strokeWidth={2}
      {...props}
    />
  );
}

export default Icon;
